// biome-ignore-all lint/suspicious/noExplicitAny: We need any here
import type { ReactNode } from 'react'
import { FormattedMessage as FMessage, useIntl as useReactIntl } from 'react-intl'
import { postTranslationSelect, useTranslationEdit } from './TranslationEditContext'
import {
  FOCALAZY_EDIT_URL,
  getTranslationDebugSettings,
  INLINE_EDIT_EVENT,
  type InlineEditEventDetail,
} from './translationDebug'

const openInlineEditPopup = (id: string, target: Element) => {
  const rect = target.getBoundingClientRect()
  const detail: InlineEditEventDetail = {
    id,
    anchor: {
      x: rect.left,
      y: rect.top,
      width: rect.width,
      height: rect.height,
    },
  }
  window.dispatchEvent(new CustomEvent(INLINE_EDIT_EVENT, { detail }))
}

type Message<K extends string, W extends string> = {
  id: string
  defaultMessage: string
  description?: string
  values?: { [key in K]: string | true }
  wrappers?: { [key in W]: string | true }
}

// Every entry must be a Message<> object — string shorthand is not accepted.
// Call sites that used to spread enum-derived string arrays need to map them
// to `{ id, defaultMessage }` first.
type ObjectFromList<K extends string, W extends string, T extends readonly Message<K, W>[]> = {
  [P in T[number]['id']]: Extract<T[number], { id: P }>
}

const listToObject = <K extends string, W extends string, T extends readonly Message<K, W>[]>(
  namespace: string,
  list: T
): ObjectFromList<K, W, T> =>
  list.reduce(
    (acc, entry) => ({
      ...acc,
      [entry.id]: { ...entry, id: `${namespace}.${entry.id}` },
    }),
    {} as ObjectFromList<K, W, T>
  )

export const defineMessages = <
  K extends string,
  W extends string,
  T extends readonly Message<K, W>[],
>(
  namespace: string,
  messages: T
): ObjectFromList<K, W, T> => {
  return listToObject(namespace, messages)
}

const getDefaultMessage = (
  messageId: string,
  values?: Record<string, any>,
  wrappers?: Record<string, any>,
  useFullId?: boolean
): string => {
  const defaultMessage = useFullId ? messageId : (messageId.split('.').pop() ?? messageId)
  const parts: string[] = [defaultMessage]

  if (values && Object.keys(values).length > 0) {
    parts.push(
      `[${Object.keys(values)
        .map(key => `(${key}): "{${key}}"`)
        .join('; ')}]`
    )
  }

  if (wrappers && Object.keys(wrappers).length > 0) {
    parts.push(
      `[${Object.keys(wrappers)
        .map(key => `<<${key}>${key}</${key}>>`)
        .join('; ')}]`
    )
  }

  return parts.join(' ')
}

const getMissingPlaceholders = (
  rawMessage: string | undefined,
  values?: Record<string, any>,
  wrappers?: Record<string, any>
): string[] => {
  if (!rawMessage) {
    return []
  }
  const missing: string[] = []
  if (values) {
    for (const key of Object.keys(values)) {
      if (!rawMessage.includes(`{${key}}`)) {
        missing.push(`{${key}}`)
      }
    }
  }
  if (wrappers) {
    for (const key of Object.keys(wrappers)) {
      if (!rawMessage.includes(`<${key}>`)) {
        missing.push(`<${key}>`)
      }
    }
  }
  return missing
}

type FormattedMessagePropsSingle<M> =
  M extends Message<infer K, infer W>
    ? M extends { values: any; wrappers: any }
      ? {
          message: M
          values: { [key in K]: ReactNode }
          wrappers: { [key in W]: (text: ReactNode) => ReactNode }
        }
      : M extends { values: any }
        ? {
            message: M
            values: { [key in K]: ReactNode }
            wrappers?: never
          }
        : M extends { wrappers: any }
          ? {
              message: M
              values?: never
              wrappers: { [key in W]: (text: ReactNode) => ReactNode }
            }
          : {
              message: M
              values?: never
              wrappers?: never
            }
    : M extends { id: string; description?: string }
      ? {
          message: M
          values?: never
          wrappers?: never
        }
      : {
          message: { id: string; description?: string }
          values?: never
          wrappers?: never
        }

type FormattedMessageProps<M> = [M] extends [{ id: string; values?: never; wrappers?: never }]
  ? {
      message: M
      values?: never
      wrappers?: never
    }
  : [M] extends [{ id: string; description?: string; values?: undefined; wrappers?: undefined }]
    ? {
        message: M
        values?: never
        wrappers?: never
      }
    : M extends any
      ? FormattedMessagePropsSingle<M>
      : never

export const FormattedMessage = <
  M extends Message<any, any> | { id: string; description?: string },
>(
  props: FormattedMessageProps<M>
): ReactNode => {
  const { message } = props
  const values = {
    ...(props.values ? props.values : {}),
    ...(props.wrappers ? props.wrappers : {}),
  }

  const debugSettings = getTranslationDebugSettings()
  const intl = useReactIntl()
  const { enabled: editEnabled } = useTranslationEdit()

  const missingWarning = debugSettings.checkVariables
    ? (() => {
        const rawMsg = intl.messages[message.id] as string | undefined
        const missing = getMissingPlaceholders(rawMsg, props.values, props.wrappers)
        if (missing.length === 0) {
          return null
        }
        return (
          <span
            className='translation-debug-trigger text-red-600 font-bold ml-1 cursor-help'
            title={`Missing: ${missing.join(', ')}`}
          >
            {'\u26A0'}
          </span>
        )
      })()
    : null

  const focalazyLink =
    debugSettings.showFocalazyLinks && !editEnabled ? (
      <a
        href={`${FOCALAZY_EDIT_URL}${message.id}`}
        target='_blank'
        rel='noopener noreferrer'
        className='translation-debug-trigger text-[10px] uppercase bg-white px-1 py-0.5 rounded border border-indigo-500 text-indigo-500 ml-1 no-underline hover:bg-indigo-50 transition-colors'
      >
        Edit
      </a>
    ) : null

  const body = debugSettings.showKeys ? (
    getDefaultMessage(message.id, props.values, props.wrappers, true)
  ) : (
    <FMessage
      id={message.id}
      values={values}
      defaultMessage={
        ('defaultMessage' in message && message.defaultMessage) ||
        getDefaultMessage(message.id, props.values, props.wrappers)
      }
    />
  )

  // Two ways to enter edit mode: (1) inside the Focalazy iframe after the
  // postMessage handshake (`editEnabled`); (2) standalone via the DevTools
  // inline-edit toggle (`debugSettings.inlineEdit`). Both share the same
  // Alt/Option-click affordance — the modifier keeps regular clicks on
  // buttons/links working untouched. The two paths only differ in what the
  // click does: iframe path posts to the parent, standalone path opens an
  // anchored popup driven by DevTools.
  const editMode = editEnabled
    ? ('iframe' as const)
    : debugSettings.inlineEdit
      ? ('inline' as const)
      : null
  if (editMode) {
    return (
      <span
        data-translation-id={message.id}
        title='Alt/Option-click to edit translation'
        onClickCapture={event => {
          if (!event.altKey) {
            return
          }
          event.preventDefault()
          event.stopPropagation()
          if (editMode === 'iframe') {
            postTranslationSelect(message.id)
          } else {
            openInlineEditPopup(message.id, event.currentTarget)
          }
        }}
        className='rounded-sm transition-colors underline decoration-dotted decoration-indigo-400/0 underline-offset-4 hover:[&]:decoration-indigo-400/70'
      >
        {body}
        {missingWarning}
      </span>
    )
  }

  const hasTrailingControls = Boolean(focalazyLink || missingWarning)
  if (!hasTrailingControls) {
    return body
  }

  return (
    <span className='inline rounded px-0.5 transition-all has-[.translation-debug-trigger:hover]:bg-indigo-200 has-[.translation-debug-trigger:hover]:ring-2 has-[.translation-debug-trigger:hover]:ring-indigo-400 has-[.translation-debug-trigger:hover]:shadow-md'>
      {body}
      {focalazyLink}
      {missingWarning}
    </span>
  )
}

export const useIntl = () => {
  const intl = useReactIntl()

  function formatMessage<K extends string, W extends string>(
    message: Message<K, W> & { values: any; wrappers: any },
    options: {
      values: { [key in K]: string | number }
      wrappers: { [key in W]: (text: string) => string }
    }
  ): string
  function formatMessage<K extends string, W extends string>(
    message: Message<K, W> & { values: any } & { wrappers?: never },
    options: {
      values: { [key in K]: string | number }
    }
  ): string
  function formatMessage<K extends string, W extends string>(
    message: Message<K, W> & { wrappers: any } & { values?: never },
    options: {
      wrappers: { [key in W]: (text: string) => string }
    }
  ): string
  function formatMessage<K extends string, W extends string>(
    message: Message<K, W> & { values?: never; wrappers?: never }
  ): string
  function formatMessage(message: {
    id: string
    description?: string
    values?: never
    wrappers?: never
  }): string
  function formatMessage(message: any, options?: any): string {
    const values = {
      ...(options?.values ? options.values : {}),
      ...(options?.wrappers ? options.wrappers : {}),
    }

    const debugSettings = getTranslationDebugSettings()

    const text = debugSettings.showKeys
      ? getDefaultMessage(message.id, options?.values, options?.wrappers, true)
      : intl.formatMessage(
          {
            id: message.id,
            defaultMessage:
              ('defaultMessage' in message && message.defaultMessage) ||
              getDefaultMessage(message.id, options?.values, options?.wrappers),
          },
          values
        )

    const missingMark = debugSettings.checkVariables
      ? (() => {
          const rawMsg = intl.messages[message.id] as string | undefined
          const missing = getMissingPlaceholders(rawMsg, options?.values, options?.wrappers)
          return missing.length > 0 ? ' [!]' : ''
        })()
      : ''

    const focalazyLink = debugSettings.showFocalazyLinks
      ? ` [${FOCALAZY_EDIT_URL}${message.id}]`
      : ''

    return `${text}${missingMark}${focalazyLink}`
  }

  return {
    ...intl,
    formatMessage,
  }
}
